import React, { useRef, useState } from "react";

import { InfoCircleOutlined } from "@ant-design/icons";
import { Input } from "antd";
import { toast } from "react-toastify";

import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import OutsideClick from "../OutsideClick";

import "./ReportsTableHeaderCell.css";
import { BLOCKED_KEY_REGEX, ALLOWED_CHAR_REGEX } from "../../../../utils/common";

import editIcon from "../../../../assets/images/product/editPencilLightBlue.png";

export default function ReportsTableHeaderCell({
  title,
  id,
  linkReportJsonFields,
  idx,
  align,
  isEditable,
  handleSaveTableTitle,
  editTableTitleIdx,
  setEditTableTitleIdx,
  innerRef,
  getDraggableStyle = () => ({}),
  draggableProps = {},
  dragHandleProps = {},
}) {
  const [isHovering, setIsHovering] = useState(false);
  const headerCellRef = useRef();
  const tappedTwice = useRef(false);
  const editingWidth = useRef();

  /**
   * Measure the width of the current cell, set editTableTitleIdx to current idx
   */
  const handleEdit = () => {
    const { width } = headerCellRef.current.getBoundingClientRect();
    editingWidth.current = width;
    setEditTableTitleIdx(idx);
  };

  /**
   * Handles double tap.
   * @returns {Boolean}
   */
  const handleDoubleTap = () => {
    if (!tappedTwice.current) {
      tappedTwice.current = true;
      setTimeout(() => { tappedTwice.current = false; }, 300);
      return false;
    }
    handleEdit();
    tappedTwice.current = false;
    return true;
  };

  /**
   * Cancel edit: clear editingWidth, set editTableTitleIdx back to -1
   */
  const handleCancelEdit = () => {
    editingWidth.current = null;
    setEditTableTitleIdx(-1);
  };

  /** on key down in input, check if key is an invalid char, if yes, don't let user input key
   * call handleInputKeyDown from props
   * @param {Event} e KeyDown event
   */
  const handleInputKeyDown = (e) => {
  // Allow navigation/editing keys
    const allowedKeys = [
      "Backspace",
      "Delete",
      "ArrowLeft",
      "ArrowRight",
      "ArrowUp",
      "ArrowDown",
      "Tab",
      "Home",
      "End",
      "Enter",
    ];

    if (allowedKeys.includes(e.key) || e.ctrlKey || e.metaKey) {
      return;
    }

    // Block special characters
    if (BLOCKED_KEY_REGEX.test(e.key)) {
      e.preventDefault();
    }
  };

  /**
   * While editing, if user hits Enter, submit the edit. If user hits Tab or Escape, cancel the edit.
   * @param {KeyDownEvent} e
   * @param {String} flag
   */
  const handleKeyDown = (e, flag) => {
    switch (e.key) {
      case "Enter":
        if (!ALLOWED_CHAR_REGEX.test(e.target.value)) {
          toast.error("Only alphabets, numbers, spaces, hyphens (-), and underscores (_) are allowed");
          return;
        }
        handleSaveTableTitle(e.target.value, flag, idx);
        break;
      case "Tab":
        handleCancelEdit();
        break;
      case "Escape":
        handleCancelEdit();
        break;
      default:
        break;
    }
  };

  const isLinkedField = idx < linkReportJsonFields.length;
  const isEditing = editTableTitleIdx === idx;
  const isItemColumn = String(id || "").toLowerCase() === "item";
  const textAlignToJustifyMap = { left: "flex-start", center: "center", right: "flex-end" };

  return (
    <th
      className={`ReportsTableHeaderCell${isLinkedField ? "--linked-field" : ""} ${isEditable ? "ReportsTableHeaderCell--editable" : ""} ${isEditing ? "ReportsTableHeaderCell--editing" : ""} ${isItemColumn ? "ReportsTableHeaderCell--item" : ""}`}
      ref={(elem) => {
        if (innerRef) {
          innerRef(elem);
        }
        headerCellRef.current = elem;
      }}
      {...draggableProps} // eslint-disable-line react/jsx-props-no-spreading
      {...dragHandleProps} // eslint-disable-line react/jsx-props-no-spreading
      style={{
        ...getDraggableStyle(),
        width: isEditing ? `${editingWidth.current}px` : draggableProps?.style?.width,
        textAlign: align,
      }}
      onDoubleClick={isEditable ? handleEdit : null}
      onTouchStart={isEditable ? handleDoubleTap : null}
      onMouseEnter={isEditable ? () => setIsHovering(true) : null}
      onMouseLeave={isEditable ? () => setIsHovering(false) : null}
    >
      {isEditing ? (
        <OutsideClick
          flag="editIndex"
          onCancelEdit={handleCancelEdit}
          className="ReportsTable__OutsideClick"
        >
          <Input
            placeholder={title}
            defaultValue={title}
            onKeyDown={(e) => {
              handleInputKeyDown(e);
              handleKeyDown(e, "editTitle");
            }}
            autoFocus
          />
        </OutsideClick>
      ) : (
        <div
          className="ReportTableHeaderCell__TitleDiv"
          style={{ justifyContent: textAlignToJustifyMap[align] }}
        >
          <span title={title}>{title}</span>
          {id === "specs_flag" && (
          <StyledTooltip
            title="The status of the final result is determined by the specification you add"
            placement="top"
          >
            <InfoCircleOutlined
              className="ReportsTableHeaderCell__InfoIcon"
              onClick={isEditable ? handleEdit : null}
              aria-label="About specification status"
            />
          </StyledTooltip>
          )}
          {isEditable && !isEditing && isHovering && (
          <img
            src={editIcon}
            alt="edit icon"
            onClick={isEditable ? handleEdit : null}
          />
          )}
        </div>
      )}
    </th>
  );
}
